import { withTranslation } from "next-i18next";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useRouter } from "next/compat/router";
import {
  useRefinementList,
  useSortBy,
  useInstantSearch,
  usePagination,
  useConfigure,
} from "react-instantsearch";
import { useCurrentUser } from "@hooks/use-current-user";
import Offcanvas from "@components/core/Offcanvas";
import Button from "@components/core/Button";
import findOrderByOptions from "@utils/catalog-filters/findOrderByOptions";
import isServer from "@utils/is-server";
import computeAppliedFiltersCount from "@utils/catalog-filters/computeAppliedFiltersCount";
import catalogContexts from "@utils/constants/catalogContexts";
import CatalogFiltersFooter from "./CatalogFiltersFooter";
import OrderBySectionBis from "./Sections/OrderBySectionBis";
import AvailabilitiesSectionBis from "./Sections/AvailabilitiesSectionBis";
import KindsSection from "./Sections/KindsSection";
import CategorySection from "./Sections/CategorySection";
import SizesSectionBis from "./Sections/SizesSectionBis";
import ColorsSection from "./Sections/ColorsSection";
import SeasonsSection from "./Sections/SeasonsSection";
import LengthSection from "./Sections/LengthSection";
import BrandsSection from "./Sections/BrandsSection";
import PersonalisedSection from "./Sections/PersonalisedSection";

const mainIndex = process.env.NEXT_PUBLIC_ALGOLIA_MAIN_INDEX_V2;

const CatalogFilters = ({
  context,
  listingPage,
  getInitializedUiState,
  shouldReinitializeUiState,
  shouldReinitializePersonalisedFilters,
  checkedPersonalised,
  filtersConfigure,
  updatePersonalisedFilters,
  userListProducts,
  t,
}) => {
  const router = useRouter();
  const user = useCurrentUser();

  const isEarlyUser = user?.early_user;

  const {
    currentRefinement: currentOrderBy,
    options: orderByOptions,
    refine: refineOrderBy,
  } = useSortBy({ items: findOrderByOptions() });
  const { items: categoryItems, refine: refineCategory } = useRefinementList({
    attribute: "category",
    limit: 20,
  });
  useRefinementList({ attribute: "catalogs" });
  useRefinementList({ attribute: "subcategory" });
  useRefinementList({ attribute: "collections" });
  const { items: inStockItems } = useRefinementList({
    attribute: "in_stock",
  });
  const { items: inStockForC1Items } = useRefinementList({
    attribute: "in_stock_for_c1",
  });
  const { items: kindsItems } = useRefinementList({ attribute: "segment" });
  const { items: allSizesItems, refine: refineAllSizes } = useRefinementList({
    attribute: "sizes",
  });
  const { items: availableSizesItems, refine: refineAvailableSizes } = useRefinementList({
    attribute: "available_sizes",
  });
  const { items: availableSizesForC1Items, refine: refineAvailableSizesForC1 } = useRefinementList({
    attribute: "available_sizes_for_c1",
  });
  const { items: colorItems, refine: refineColor } = useRefinementList({
    attribute: "colors",
    limit: 20,
  });
  const { items: seasonItems, refine: refineSeason } = useRefinementList({
    attribute: "season",
  });
  const { items: sleeveItems, refine: refineSleeve } = useRefinementList({
    attribute: "sleeves",
  });
  const { items: legItems, refine: refineLeg } = useRefinementList({
    attribute: "legs",
  });
  const { items: brandItems, refine: refineBrand } = useRefinementList({
    attribute: "brand.name",
    limit: 200,
    sortBy: ["name:asc"],
  });
  useRefinementList({ attribute: "id" });

  const isNotInStockApplied = useMemo(() => {
    return inStockItems.find((item) => item.label === "false")?.isRefined;
  }, [inStockItems]);
  const isNotInStockForC1Applied = useMemo(() => {
    return inStockForC1Items.find((item) => item.label === "false")?.isRefined;
  }, [inStockForC1Items]);

  const [isFilterAvailableChecked, setIsFilterAvailableChecked] = useState(
    user?.c1_dedicated ? !isNotInStockForC1Applied : !isNotInStockApplied,
  );

  const hasLargeSizes = useMemo(() => {
    const extractSizes = (items) => {
      return items
        .filter((sizeItem) => sizeItem.isRefined && sizeItem.value !== "TU")
        .map((sizeItem) => sizeItem.value);
    };

    let appliedSizes = [];

    if (isFilterAvailableChecked) {
      if (user?.c1_dedicated) {
        appliedSizes = extractSizes(availableSizesForC1Items);
      } else {
        appliedSizes = extractSizes(availableSizesItems);
      }
    } else {
      appliedSizes = extractSizes(allSizesItems);
    }

    const numericSizes = appliedSizes
      .map((size) => parseInt(size, 10))
      .filter((size) => !isNaN(size));

    return numericSizes.length > 0 && numericSizes.every((size) => size >= 42);
  }, [
    allSizesItems,
    isFilterAvailableChecked,
    user?.c1_dedicated,
    availableSizesForC1Items,
    availableSizesItems,
  ]);

  useConfigure({
    userToken: user?.uid,
    getRankingInfo: true,
    filters: filtersConfigure,
    ruleContexts: isEarlyUser
      ? hasLargeSizes
        ? ["early_user_large_size"]
        : ["early_user"]
      : ["not_early_user"],
  });

  const { uiState, setUiState } = useInstantSearch();
  const { nbHits: resultsCount } = usePagination();

  const [open, setOpen] = useState(false);
  const [filterSectionOpened, setFilterSectionOpened] = useState("");

  const computedAppliedFiltersCount = useMemo(
    () =>
      computeAppliedFiltersCount(
        listingPage,
        uiState[mainIndex]?.refinementList,
        checkedPersonalised,
      ),
    [listingPage, uiState, mainIndex, checkedPersonalised],
  );

  const closeSectionAndFilters = useCallback(() => {
    setFilterSectionOpened("");
    setOpen(false);
  }, []);

  // TO DO: Refactor this duplicate code into hook (L71 > L77)
  const clearFiltersAndClose = useCallback(() => {
    const resetedUiState = getInitializedUiState({});
    setUiState(resetedUiState);
    if (shouldReinitializePersonalisedFilters()) updatePersonalisedFilters("all", "");

    closeSectionAndFilters();
  }, [
    closeSectionAndFilters,
    getInitializedUiState,
    setUiState,
    shouldReinitializePersonalisedFilters,
    updatePersonalisedFilters,
  ]);

  useEffect(() => {
    function reinitiliazeUiStateOnSamePageNavigation() {
      if (isServer() || !shouldReinitializeUiState(context, router.query, uiState, listingPage))
        return;

      const resetedUiState = getInitializedUiState(window.location);
      setUiState(resetedUiState);
      if (shouldReinitializePersonalisedFilters()) updatePersonalisedFilters("all", "");
    }

    reinitiliazeUiStateOnSamePageNavigation();
  }, [router]);

  useEffect(() => {
    function reinitiliazeUiStateUserListProductsChange() {
      if (isServer() || context !== catalogContexts.userlist) return;

      const resetedUiState = getInitializedUiState(window.location);
      setUiState(resetedUiState);
      if (shouldReinitializePersonalisedFilters()) updatePersonalisedFilters("all", "");
    }

    reinitiliazeUiStateUserListProductsChange();
  }, [userListProducts]);

  useEffect(() => {
    function resetFilterAvailableChecked() {
      const newIsFilterAvailableChecked = user?.c1_dedicated
        ? !isNotInStockForC1Applied
        : !isNotInStockApplied;

      if (isFilterAvailableChecked !== newIsFilterAvailableChecked) {
        setIsFilterAvailableChecked(newIsFilterAvailableChecked);
      }
    }

    resetFilterAvailableChecked();
  }, [isNotInStockForC1Applied, isNotInStockApplied]);

  return (
    <>
      <Button
        variant="ghost-dark"
        leftIconName="filter-and-order-icon"
        data-testid="catalog-mobile-filters-button"
        onClick={() => setOpen(true)}
        hugContent
        className="bg-white"
        size="sm"
        trackingName="filterButton"
      >
        {t("catalog:filters.title", { count: computedAppliedFiltersCount })}
      </Button>

      <Offcanvas
        show={open}
        onHide={closeSectionAndFilters}
        title={t("catalog:filters.title", { count: 0 })}
        placement="end"
        Footer={
          <CatalogFiltersFooter
            clearFiltersAndClose={clearFiltersAndClose}
            closeSectionAndFilters={closeSectionAndFilters}
            resultsCount={resultsCount}
          />
        }
      >
        <div className="catalog-filters-body">
          <div className="bg-white">
            <OrderBySectionBis
              context={context}
              currentOrderBy={currentOrderBy}
              orderByOptions={orderByOptions}
              refineOrderBy={refineOrderBy}
            />

            <AvailabilitiesSectionBis
              setUiState={setUiState}
              isFilterAvailableChecked={isFilterAvailableChecked}
              setIsFilterAvailableChecked={setIsFilterAvailableChecked}
            />
            <KindsSection
              kindsItems={kindsItems}
              uiState={uiState}
              setUiState={setUiState}
              listingPage={listingPage}
              onClose={closeSectionAndFilters}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <CategorySection
              categoryItems={categoryItems}
              refineCategory={refineCategory}
              listingPage={listingPage}
              onClose={closeSectionAndFilters}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />

            <SizesSectionBis
              listingPage={listingPage}
              onClose={closeSectionAndFilters}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
              allSizesItems={allSizesItems}
              availableSizesItems={availableSizesItems}
              availableSizesForC1Items={availableSizesForC1Items}
              refineAllSizes={refineAllSizes}
              refineAvailableSizes={refineAvailableSizes}
              refineAvailableSizesForC1={refineAvailableSizesForC1}
              isFilterAvailableChecked={isFilterAvailableChecked}
            />
            <ColorsSection
              colorItems={colorItems}
              refineColor={refineColor}
              listingPage={listingPage}
              onClose={closeSectionAndFilters}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <SeasonsSection
              seasonItems={seasonItems}
              refineSeason={refineSeason}
              listingPage={listingPage}
              onClose={closeSectionAndFilters}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <LengthSection
              sleeveItems={sleeveItems}
              legItems={legItems}
              refineSleeve={refineSleeve}
              refineLeg={refineLeg}
              listingPage={listingPage}
              onClose={closeSectionAndFilters}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <BrandsSection
              brandItems={brandItems}
              refineBrand={refineBrand}
              listingPage={listingPage}
              onClose={closeSectionAndFilters}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <PersonalisedSection
              listingPage={listingPage}
              onClose={closeSectionAndFilters}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
              checkedPersonalised={checkedPersonalised}
              updatePersonalisedFilters={updatePersonalisedFilters}
            />
          </div>
        </div>
      </Offcanvas>
    </>
  );
};

export default withTranslation("catalog")(CatalogFilters);
